// ============================================================================
// S3 BROWSER TAB COMPONENT
// Browse S3 bucket contents with folder-style navigation
// ============================================================================

import React, { useState, useRef, useMemo } from 'react';
import { Folder, File, Download, ChevronRight, ChevronLeft, Home, RefreshCw, AlertCircle, Trash2, Upload, Search } from 'lucide-react';
import { useS3Browse, downloadS3File, useDeleteS3Objects, useUploadS3File, S3File, S3Folder } from '../../hooks/useS3Browser';
import { ConfirmModal } from '../common/ConfirmModal';
import { useAuth } from '../../contexts/AuthContext';

interface S3BrowserTabProps {
  projectId: string;
  token: string;
  projectOwnerId: string;
}

export const S3BrowserTab: React.FC<S3BrowserTabProps> = ({ projectId, token, projectOwnerId }) => {
  const { user } = useAuth();
  const isOwner = user?.userId === projectOwnerId;

  const [currentPrefix, setCurrentPrefix] = useState<string>(`projects/${projectId}/`);
  const [breadcrumbs, setBreadcrumbs] = useState<{ name: string; path: string }[]>([
    { name: 'Project Root', path: `projects/${projectId}/` }
  ]);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [customPrefix, setCustomPrefix] = useState<string>('');
  const [, setUploadProgress] = useState<{ [key: string]: number }>({});
  const [searchQuery, setSearchQuery] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, error, refetch } = useS3Browse(projectId, token, currentPrefix);
  const deleteMutation = useDeleteS3Objects(projectId, token);
  const uploadMutation = useUploadS3File(projectId, token);

  // ============================================================================
  // PAGINATION & FILTERING
  // ============================================================================

  const allItems = useMemo(() => {
    if (!data) return [];
    const items = [...data.folders, ...data.files];
    if (!searchQuery) return items;
    const query = searchQuery.toLowerCase();
    return items.filter(item => item.name.toLowerCase().includes(query));
  }, [data, searchQuery]);

  const totalItems = allItems.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedItems = allItems.slice(startIndex, endIndex);

  const paginatedFolders = paginatedItems.filter(item => item.type === 'folder') as S3Folder[];
  const paginatedFiles = paginatedItems.filter(item => item.type === 'file') as S3File[];

  React.useEffect(() => {
    setCurrentPage(1);
    setSelectedKeys(new Set());
  }, [currentPrefix]);

  const isAllOnPageSelected = paginatedItems.length > 0 &&
    paginatedItems.every(item => selectedKeys.has(item.fullPath));

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  // ============================================================================
  // NAVIGATION HANDLERS
  // ============================================================================

  const navigateToFolder = (folder: S3Folder) => {
    setCurrentPrefix(folder.fullPath);
    setBreadcrumbs([...breadcrumbs, { name: folder.name, path: folder.fullPath }]);
    setSelectedKeys(new Set());
  };

  const navigateToBreadcrumb = (index: number) => {
    const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
    setBreadcrumbs(newBreadcrumbs);
    setCurrentPrefix(newBreadcrumbs[newBreadcrumbs.length - 1].path);
    setSelectedKeys(new Set());
  };

  const navigateToRoot = () => {
    const rootPath = `projects/${projectId}/`;
    setCurrentPrefix(rootPath);
    setBreadcrumbs([{ name: 'Project Root', path: rootPath }]);
    setSelectedKeys(new Set());
  };

  // ============================================================================
  // FILE HANDLERS
  // ============================================================================

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleDownload = async (file: S3File) => {
    try {
      const downloadUrl = await downloadS3File(projectId, file.fullPath, token);
      window.open(downloadUrl, '_blank');
    } catch (err) {
      console.error('Download error:', err);
      showNotification('error', 'Failed to download file. Please try again.');
    }
  };

  // ============================================================================
  // SELECTION HANDLERS
  // ============================================================================

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const newSelected = new Set(selectedKeys);
      paginatedItems.forEach(item => newSelected.add(item.fullPath));
      setSelectedKeys(newSelected);
    } else {
      const newSelected = new Set(selectedKeys);
      paginatedItems.forEach(item => newSelected.delete(item.fullPath));
      setSelectedKeys(newSelected);
    }
  };

  const handleSelectItem = (key: string, checked: boolean) => {
    const newSelected = new Set(selectedKeys);
    if (checked) {
      newSelected.add(key);
    } else {
      newSelected.delete(key);
    }
    setSelectedKeys(newSelected);
  };

  const handleBulkDelete = () => {
    if (selectedKeys.size === 0) return;
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (selectedKeys.size === 0) return;
    try {
      const keysArray = Array.from(selectedKeys);
      const result = await deleteMutation.mutateAsync(keysArray);
      showNotification('success', `Successfully deleted ${result.deletedCount} object${result.deletedCount !== 1 ? 's' : ''}`);
      setDeleteConfirmOpen(false);
      setSelectedKeys(new Set());
      refetch();
    } catch (error: any) {
      showNotification('error', `Delete failed: ${error.response?.data?.error || error.message}`);
      setDeleteConfirmOpen(false);
    }
  };

  // ============================================================================
  // UPLOAD HANDLERS
  // ============================================================================

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      setSelectedFiles(fileArray);
      const projectPrefix = `projects/${projectId}/`;
      const relativePath = currentPrefix.startsWith(projectPrefix)
        ? currentPrefix.substring(projectPrefix.length)
        : '';
      setCustomPrefix(relativePath);
      setUploadModalOpen(true);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadConfirm = async () => {
    if (selectedFiles.length === 0) return;

    setIsUploading(true);
    setUploadModalOpen(false);

    const projectPrefix = `projects/${projectId}/`;
    const fullPrefix = customPrefix ? `${projectPrefix}${customPrefix}` : projectPrefix;

    let successCount = 0;
    let errorCount = 0;

    for (const file of selectedFiles) {
      try {
        await uploadMutation.mutateAsync({ file, prefix: fullPrefix });
        successCount++;
        setUploadProgress(prev => ({ ...prev, [file.name]: 100 }));
      } catch (error: any) {
        errorCount++;
        console.error(`Upload failed for ${file.name}:`, error);
      }
    }

    if (errorCount === 0) {
      showNotification('success', `${successCount} file${successCount !== 1 ? 's' : ''} uploaded successfully`);
    } else if (successCount === 0) {
      showNotification('error', `Failed to upload ${errorCount} file${errorCount !== 1 ? 's' : ''}`);
    } else {
      showNotification('success', `${successCount} file${successCount !== 1 ? 's' : ''} uploaded, ${errorCount} failed`);
    }

    setSelectedFiles([]);
    setUploadProgress({});
    setIsUploading(false);
    refetch();
  };

  const handleUploadCancel = () => {
    setUploadModalOpen(false);
    setSelectedFiles([]);
    setCustomPrefix('');
  };

  // ============================================================================
  // PAGINATION HELPERS
  // ============================================================================

  const getPageNumbers = () => {
    const pages: number[] = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (currentPage <= 3) {
      for (let i = 1; i <= maxVisible; i++) pages.push(i);
    } else if (currentPage >= totalPages - 2) {
      for (let i = totalPages - maxVisible + 1; i <= totalPages; i++) pages.push(i);
    } else {
      for (let i = currentPage - 2; i <= currentPage + 2; i++) pages.push(i);
    }
    return pages;
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="flex flex-col gap-6">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-20 right-5 z-50 px-4 py-3 rounded-lg text-sm font-medium shadow-lg ${
          notification.type === 'success'
            ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {notification.message}
        </div>
      )}

      {/* Breadcrumb Bar */}
      <div className="flex items-center gap-2 px-4 py-3 bg-slate-100 rounded-lg flex-wrap">
        <button
          onClick={navigateToRoot}
          className="text-primary hover:underline text-sm font-medium flex items-center gap-1"
        >
          <Home className="w-4 h-4" /> Home
        </button>
        {breadcrumbs.map((crumb, index) => (
          <React.Fragment key={crumb.path}>
            <ChevronRight className="w-4 h-4 text-slate-400" />
            {index === breadcrumbs.length - 1 ? (
              <span className="text-navy text-sm font-semibold">{crumb.name}</span>
            ) : (
              <button
                onClick={() => navigateToBreadcrumb(index)}
                className="text-primary hover:underline text-sm font-medium"
              >
                {crumb.name}
              </button>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Action Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {isOwner && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />
              <button
                onClick={handleUploadClick}
                disabled={isUploading || isLoading}
                className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-white font-semibold rounded-lg transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Upload className="w-4 h-4" />
                <span>{isUploading ? 'Uploading...' : 'Upload Files'}</span>
              </button>
            </>
          )}
          <button
            onClick={() => refetch()}
            disabled={isLoading}
            className="p-2 bg-white border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-all disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
              placeholder="Search files and folders..."
              className="pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary w-64"
            />
          </div>
        </div>
      </div>

      {/* Upload Progress */}
      {isUploading && (
        <div className="flex items-center gap-3 rounded-xl border border-blue-100 bg-blue-50 p-4">
          <Upload className="w-5 h-5 text-blue-600" />
          <div>
            <p className="text-blue-900 text-sm font-bold">Uploading files...</p>
            <p className="text-blue-700 text-xs">Uploading to {customPrefix || '(project root)'}</p>
          </div>
        </div>
      )}

      {/* Bulk Actions Bar */}
      {isOwner && selectedKeys.size > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 rounded-xl border border-blue-100 bg-blue-50 p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="size-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
              <AlertCircle className="w-4 h-4" />
            </div>
            <div>
              <p className="text-blue-900 text-sm font-bold">
                {selectedKeys.size} item{selectedKeys.size !== 1 ? 's' : ''} selected
              </p>
              <p className="text-blue-700 text-xs">Bulk actions available for selected files</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelectedKeys(new Set())}
              className="text-blue-700 text-sm font-medium hover:underline px-3 py-1"
            >
              Clear Selection
            </button>
            <button
              onClick={handleBulkDelete}
              className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white text-sm font-bold rounded-lg hover:bg-red-600 transition-all"
            >
              <Trash2 className="w-4 h-4" />
              <span>Delete Selected</span>
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-16 text-slate-400">
          <RefreshCw className="w-8 h-8 mx-auto animate-spin mb-3" />
          <p className="text-sm">Loading files...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <div>
            <p className="text-red-800 font-medium text-sm">Failed to load S3 contents</p>
            <p className="text-red-600 text-xs mt-1">
              {error instanceof Error ? error.message : 'Unknown error occurred'}
            </p>
          </div>
        </div>
      )}

      {/* Content */}
      {!isLoading && !error && data && (
        <>
          {/* Empty State */}
          {data.folders.length === 0 && data.files.length === 0 && (
            <div className="text-center py-16 text-slate-400">
              <Folder className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-base font-medium">This folder is empty</p>
              <p className="text-sm mt-2">No files or folders found in this location.</p>
            </div>
          )}

          {/* Files/Folders Table */}
          {(data.folders.length > 0 || data.files.length > 0) && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    {isOwner && (
                      <th className="p-4 w-10">
                        <input
                          type="checkbox"
                          checked={isAllOnPageSelected}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          className="rounded text-primary focus:ring-primary border-slate-300 h-4 w-4"
                        />
                      </th>
                    )}
                    <th className="p-4 text-navy font-semibold text-xs uppercase tracking-wider">Name</th>
                    <th className="p-4 text-navy font-semibold text-xs uppercase tracking-wider w-28">Size</th>
                    <th className="p-4 text-navy font-semibold text-xs uppercase tracking-wider w-48">Date Modified</th>
                    <th className="p-4 w-20"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {/* Folders */}
                  {paginatedFolders.map((folder) => (
                    <tr
                      key={folder.fullPath}
                      className={`hover:bg-slate-50 transition-colors group ${
                        selectedKeys.has(folder.fullPath) ? 'bg-blue-50/50' : ''
                      }`}
                    >
                      {isOwner && (
                        <td className="p-4">
                          <input
                            type="checkbox"
                            checked={selectedKeys.has(folder.fullPath)}
                            onChange={(e) => handleSelectItem(folder.fullPath, e.target.checked)}
                            onClick={(e) => e.stopPropagation()}
                            className="rounded text-primary focus:ring-primary border-slate-300 h-4 w-4"
                          />
                        </td>
                      )}
                      <td className="p-4 cursor-pointer" onClick={() => navigateToFolder(folder)}>
                        <div className="flex items-center gap-3">
                          <Folder className="w-5 h-5 text-blue-400" />
                          <span className="text-primary font-semibold hover:underline">{folder.name}</span>
                        </div>
                      </td>
                      <td className="p-4 text-slate-400 text-sm">--</td>
                      <td className="p-4 text-slate-400 text-sm">--</td>
                      <td className="p-4"></td>
                    </tr>
                  ))}

                  {/* Files */}
                  {paginatedFiles.map((file) => (
                    <tr
                      key={file.fullPath}
                      className={`hover:bg-slate-50 transition-colors group ${
                        selectedKeys.has(file.fullPath) ? 'bg-blue-50/50' : ''
                      }`}
                    >
                      {isOwner && (
                        <td className="p-4">
                          <input
                            type="checkbox"
                            checked={selectedKeys.has(file.fullPath)}
                            onChange={(e) => handleSelectItem(file.fullPath, e.target.checked)}
                            onClick={(e) => e.stopPropagation()}
                            className="rounded text-primary focus:ring-primary border-slate-300 h-4 w-4"
                          />
                        </td>
                      )}
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <File className="w-5 h-5 text-slate-400" />
                          <span className="text-navy font-medium">{file.name}</span>
                        </div>
                      </td>
                      <td className="p-4 text-slate-500 text-sm">{formatFileSize(file.size)}</td>
                      <td className="p-4 text-slate-500 text-sm">{formatDate(file.lastModified)}</td>
                      <td className="p-4 text-right">
                        <button
                          onClick={() => handleDownload(file)}
                          className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-primary transition-all p-1"
                        >
                          <Download className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between p-4 bg-slate-50 border-t border-slate-200">
                  <div className="flex items-center gap-4">
                    <p className="text-slate-500 text-sm">
                      Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems} items
                    </p>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                      className="text-sm border border-slate-200 rounded-lg px-2 py-1 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/30"
                    >
                      <option value="10">10 per page</option>
                      <option value="25">25 per page</option>
                      <option value="50">50 per page</option>
                      <option value="100">100 per page</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="p-2 text-slate-400 hover:bg-slate-200 rounded transition-all disabled:opacity-50"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    {getPageNumbers().map(pageNum => (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`w-8 h-8 flex items-center justify-center rounded text-sm font-medium transition-all ${
                          currentPage === pageNum
                            ? 'bg-primary text-white font-bold'
                            : 'text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {pageNum}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="p-2 text-slate-400 hover:bg-slate-200 rounded transition-all disabled:opacity-50"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Truncation Warning */}
          {data.isTruncated && (
            <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              <span className="text-amber-800 text-sm">
                This folder contains more items than can be displayed at once. Navigate through subfolders to see all content.
              </span>
            </div>
          )}
        </>
      )}

      {/* Upload Modal */}
      {uploadModalOpen && selectedFiles.length > 0 && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-lg w-[90%] max-h-[80vh] overflow-auto shadow-xl">
            <h3 className="text-lg font-bold text-navy mb-4">
              Upload {selectedFiles.length} File{selectedFiles.length !== 1 ? 's' : ''}
            </h3>

            <div className="mb-5 max-h-48 overflow-y-auto space-y-2">
              {selectedFiles.map((file) => (
                <div key={file.name} className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                  <Upload className="w-5 h-5 text-slate-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-navy truncate">{file.name}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{formatFileSize(file.size)}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mb-5">
              <label className="block text-sm font-medium text-navy mb-2">
                Upload Path (relative to project)
              </label>
              <input
                type="text"
                value={customPrefix}
                onChange={(e) => setCustomPrefix(e.target.value)}
                placeholder="folder/subfolder/"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
              <p className="text-xs text-slate-500 mt-1.5">
                Files will be uploaded to: <strong>{customPrefix || '(project root)'}</strong>
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={handleUploadCancel}
                className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleUploadConfirm}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 text-sm font-medium"
              >
                <Upload className="w-4 h-4" />
                Upload
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Files"
        message={`Are you sure you want to delete ${selectedKeys.size} item${selectedKeys.size !== 1 ? 's' : ''}? This action cannot be undone. ${Array.from(selectedKeys).some(k => k.endsWith('/')) ? 'Folders will be deleted recursively with all their contents.' : ''}`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isProcessing={deleteMutation.isPending}
      />
    </div>
  );
};
